'use client';

import './bootcamp-sidebar.less';

import { HelpCircle, Mail, ArrowLeft, CheckCircle, X, Check } from 'lucide-react';
import type { CollegeChapterItem } from '@zrt-ai-office/types/course';
import { useToast } from '@/lib/hooks/use-toast';

interface BootcampSidebarProps {
	courseTitle: string;
	chapters: CollegeChapterItem[];
	activeChapterId: number | null;
	onSelectChapter: (id: number) => void;
	onContinueLearning?: () => void;
	onBackToCourseList?: () => void;
	allCompleted?: boolean;
	mobileOpen?: boolean;
	onMobileClose?: () => void;
}

export function BootcampSidebar({
	courseTitle,
	chapters,
	activeChapterId,
	onSelectChapter,
	onContinueLearning,
	onBackToCourseList,
	allCompleted,
	mobileOpen,
	onMobileClose,
}: BootcampSidebarProps) {
	const toast = useToast();

	const nextChapter = chapters.find((ch) => !ch.learned);

	const handleSelectChapter = (id: number) => {
		onSelectChapter(id);
		if (onMobileClose) onMobileClose();
	};

	const sidebarContent = (
		<>
			<div className="bootcamp-sidebar__header">
				<div className="bootcamp-sidebar__title-row">
					{onBackToCourseList && (
						<button
							className="bootcamp-sidebar__back-btn"
							onClick={onBackToCourseList}
							type="button"
						>
							<ArrowLeft className="h-4 w-4" />
						</button>
					)}
					<h2 className="bootcamp-sidebar__title">{courseTitle}</h2>
					{onMobileClose && (
						<button className="bootcamp-sidebar__close-btn" onClick={onMobileClose} type="button">
							<X className="h-4 w-4" />
						</button>
					)}
				</div>

				{allCompleted ? (
					<div className="bootcamp-sidebar__completed-badge">
						<CheckCircle className="bootcamp-sidebar__completed-icon" />
						<span>已全部完成</span>
					</div>
				) : (
					<button
						className="bootcamp-sidebar__cta"
						onClick={() => {
							if (onContinueLearning) onContinueLearning();
							else if (nextChapter) handleSelectChapter(nextChapter.id);
						}}
						type="button"
					>
						继续学习
					</button>
				)}
			</div>

			<div className="bootcamp-sidebar__separator" />

			<nav className="bootcamp-sidebar__nav">
				<ul className="bootcamp-sidebar__chapter-list">
					{chapters.map((chapter, index) => {
						const isActive = chapter.id === activeChapterId;
						const isLearned = chapter.learned;

						return (
							<li key={chapter.id}>
								<button
									className={`bootcamp-sidebar__chapter-btn ${isActive ? 'bootcamp-sidebar__chapter-btn--active' : ''}`}
									onClick={() => handleSelectChapter(chapter.id)}
									type="button"
								>
									<span className="bootcamp-sidebar__chapter-indicator">
										{isLearned && (
											<Check className="bootcamp-sidebar__chapter-check" />
										)}
										<span
											className={`bootcamp-sidebar__chapter-index ${isLearned ? 'bootcamp-sidebar__chapter-index--completed' : isActive ? 'bootcamp-sidebar__chapter-index--active' : ''}`}
										>
											{index + 1}
										</span>
									</span>

									<div className="bootcamp-sidebar__chapter-info">
										<p
											className={`bootcamp-sidebar__chapter-title ${isActive ? 'bootcamp-sidebar__chapter-title--active' : ''} ${isLearned ? 'bootcamp-sidebar__chapter-title--completed' : ''}`}
										>
											{chapter.title}
										</p>
										{chapter.intro && <p className="bootcamp-sidebar__chapter-intro">{chapter.intro}</p>}
									</div>
								</button>
							</li>
						);
					})}
				</ul>
			</nav>

			<div className="bootcamp-sidebar__footer">
				<button className="bootcamp-sidebar__footer-link" onClick={() => toast.info('帮助中心正在建设中，敬请期待')} type="button">
					<HelpCircle className="bootcamp-sidebar__footer-link-icon" />
					帮助中心
				</button>
				<button className="bootcamp-sidebar__footer-link" onClick={() => toast.info('联系支持功能即将上线，敬请期待')} type="button">
					<Mail className="bootcamp-sidebar__footer-link-icon" />
					联系支持
				</button>
			</div>
		</>
	);

	return (
		<>
			{/* Desktop sidebar */}
			<aside className="bootcamp-sidebar">{sidebarContent}</aside>

			{/* Mobile overlay */}
			{mobileOpen && (
				<div className="bootcamp-sidebar__overlay" onClick={onMobileClose}>
					<aside className="bootcamp-sidebar bootcamp-sidebar--mobile" onClick={(e) => e.stopPropagation()}>
						{sidebarContent}
					</aside>
				</div>
			)}
		</>
	);
}
